import styles from "./ArticleLeadArt.module.scss";

import {
  ArticleLayout,
  ArticleLeadImageFragment,
  ArticleLeadImageLargeFragment,
  ArticleLeadImageVerticalFragment,
  ArticleLeadCinemagraphPosterFragment,
} from "types/api";
import ArticleLeadFigure from "components/article/ArticleLeadFigure";
import Image from "components/Image";
import { gql } from "utils/urql";
import cx from "classnames";

const ArticleLeadImage = ({
  layout,
  className,
  lazy,
  ...props
}: {
  layout: ArticleLayout;
  className?: string;
  lazy?: boolean;
} & (
  | ArticleLeadImageFragment
  | ArticleLeadImageLargeFragment
  | ArticleLeadImageVerticalFragment
  | ArticleLeadCinemagraphPosterFragment
)) => {
  const image =
    props.__typename === "BasicImage"
      ? props
      : props.__typename === "LeadArtImage" ||
        props.__typename === "LeadArtImageLarge" ||
        props.__typename === "LeadArtImageVertical"
      ? props.image
      : null;
  return (
    <>
      {image && (
        <ArticleLeadFigure layout={layout} {...image}>
          <Image
            className={cx(
              styles.image,
              layout === ArticleLayout.Feature && styles.featureMedia,
              className
            )}
            src={image.url}
            srcSet={image.srcSet || undefined}
            width={image.width}
            height={image.height}
            alt={image.altText || ""}
            lazy={lazy || false}
            preload={true}
            /**
             * min-width's are breakpoints, starting with mobile first
             * 100vw tells browsers the image is fluid below $breakpoint-article-nav
             * Above $breakpoint-article-nav, art is fixed at 976px
             */
            sizes={
              (layout === ArticleLayout.Standard &&
                [`(min-width: 976px) 976px`, "100vw"].join(", ")) ||
              (layout === ArticleLayout.Feature &&
                [`(min-width: 1920px) 1920px`, "100vw"].join(", ")) ||
              (layout === ArticleLayout.Twocol &&
                [
                  "(min-width: 1440px) 656px",
                  "(min-width:1024px) calc(50vw - 64px)",
                  "(min-width: 768px) calc(50vw - 48px)",
                  "(min-width: 375px) calc(100vw - 103px)",
                  "(min-width: 320px) calc(100vw - 64px)",
                  "calc(100vw - 48px)",
                ].join(", ")) ||
              ""
            }
          />
        </ArticleLeadFigure>
      )}
    </>
  );
};

ArticleLeadImage.fragment = gql`
  fragment articleLeadArtImageProps on BasicImage {
    url
    width
    height
    srcSet
    captionText
    attributionText
    attributionUrl
    altText
  }
  fragment articleLeadImage on LeadArtImage {
    image(
      width: 960
      height: 540
      # 750w = 375px * 2 (mobile retina)
      # 828w (mobile large retina)
      # 960w (smallest size available for desktop)
      # 976w (default size for desktop & desktop large)
      # 1952w = 976 * 2 (desktop retina)
      sizes: ["750w", "828w", "960w", "976w", "1952w"]
    ) {
      ...articleLeadArtImageProps
    }
  }
  fragment articleLeadImageLarge on LeadArtImageLarge {
    image(
      # fix aspect ratio, but let srcset & sizes load larger if possible
      width: 1440
      height: 810
      # Sizes based off of default inspector responsive sizes
      # 640 = 320px * 2 (mobile small retina)
      # 750w = 375 * 2 (mobile retina)
      # 850w = 425 * 2 (mobile large retina)
      # 1536w = 768 * 2 (tablet retina)
      # 1920w = (standard lead art large size)
      # 2048w = 1024 * 2 (laptop retina)
      # 2880w = 1440 * 2 (laptop large retina)
      # "3840w" = 1920 * 2 (standard lead art retina)
      sizes: [
        "640w"
        "750w"
        "850w"
        "1536w"
        "1920w"
        "2048w"
        "2880w"
        "3840w"
      ]
    ) {
      ...articleLeadArtImageProps
    }
  }
  fragment articleLeadImageVertical on LeadArtImageVertical {
    image(
      width: 648
      height: 810
      # 296w, 592w = calc(100vw - 64px) (fullwidth tall image with 32px margins on each side)
      #   on a 360px wide screen (common mobile device screen width) at 1x & 2x for mobile retina
      # 311w, 622w = calc(100vw - 103px) (fullwidth tall image with side margins of 51 and 52px)
      #   on a 414px wide screen (common mobile device screen width) at 1x & 2x for mobile retina
      # 665w, 1330w = 665px is the widest the tall image can be on any screen, and 2x for mobile retina
      # 960w = the minimum size for the lead image incase 1330w is too big
      sizes: ["296w", "311w", "592w", "622w", "665w", "960w", "1330w"]
    ) {
      ...articleLeadArtImageProps
    }
  }
`;

export default ArticleLeadImage;
